// Locale-aware formatting for the transactions UI.
//
// Datetime: the API sends TransactionDateTimeLocal already converted to the location's wall clock, as a
// bare ISO with no offset (e.g. "2026-07-25T14:30:00"). We pin it to UTC and format with timeZone: 'UTC' so
// the wall-clock components render unchanged regardless of the viewer's browser timezone.
//
// Currency: the transaction carries a locale but no explicit currency code, so we derive the currency from
// the locale's region. This is a best-effort map of the regions TouchTab operates in; unknown regions fall
// back to USD.

const REGION_CURRENCY: Record<string, string> = {
    US: 'USD', CA: 'CAD', GB: 'GBP', IE: 'EUR', AU: 'AUD', NZ: 'NZD',
    FR: 'EUR', DE: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', PT: 'EUR', BE: 'EUR', AT: 'EUR', FI: 'EUR', GR: 'EUR',
    JP: 'JPY', CN: 'CNY', HK: 'HKD', SG: 'SGD', IN: 'INR', MX: 'MXN', BR: 'BRL',
    CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', ZA: 'ZAR', AE: 'AED',
};

const safeLocale = (locale?: string | null): string => {
    if (!locale) return 'en-US';
    try {
        // Throws on a malformed tag.
        void new Intl.Locale(locale);
        return locale;
    } catch {
        return 'en-US';
    }
};

const currencyForLocale = (locale?: string | null): string => {
    try {
        const region = new Intl.Locale(safeLocale(locale)).region;
        return (region && REGION_CURRENCY[region]) || 'USD';
    } catch {
        return 'USD';
    }
};

// cents -> localized currency string.
export const formatMoney = (cents: number, locale?: string | null): string =>
    new Intl.NumberFormat(safeLocale(locale), { style: 'currency', currency: currencyForLocale(locale) })
        .format((cents ?? 0) / 100);

// location-local bare-ISO -> localized date/time string, components preserved.
export const formatDateTime = (localIso: string, locale?: string | null): string => {
    if (!localIso) return '';
    const asUtc = localIso.endsWith('Z') ? localIso : `${localIso}Z`;
    const d = new Date(asUtc);
    if (isNaN(d.getTime())) return localIso;
    return new Intl.DateTimeFormat(safeLocale(locale), { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(d);
};
